import { useMutation } from "@apollo/client";
import CloseIcon from "@mui/icons-material/Close";
import ErrorOutline from "@mui/icons-material/ErrorOutline";
import PublishIcon from "@mui/icons-material/Publish";
import {
  Box,
  Button,
  colors,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  GridLegacy as Grid,
  IconButton,
  InputLabel,
  ListItem,
  ListItemText,
  Paper,
  Skeleton,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";
import { useAlerts } from "components/Alerts/AlertProvider";
import {
  ReportHasNoChemicalsAlert,
  useHasChemicalsToReport,
} from "components/Alerts/ReportHasNoChemicalsAlert";
import { ConfirmDialog } from "components/ConfirmDialog";
import { CheckboxField } from "components/Forms/CheckboxField";
import { DateField } from "components/Forms/DateField";
import { FormTextField } from "components/Forms/FormTextField";
import { OmnisearchAutocomplete } from "components/OmnisearchAutocomplete";
import { PERSON_QUERY } from "components/VerifierTable";
import { currentTierIIReportingYear } from "encamp-shared/src/constants/tierii";
import { shouldAllowSubmission } from "encamp-shared/src/tierii/submissionHelpers";
import { gql } from "generated-graphql";
import {
  GetReportDetailsQuery,
  NotReportingReason,
  Permission,
  Phone,
  PhoneType,
  TierIiReportKind,
  TierIiReportStep,
  TierIiReportVerifier,
  VerifiersQuery,
} from "generated-graphql/graphql";
import { useAuthorization } from "hooks/useAuthorization";
import { useCurrentUser } from "hooks/useCurrentUser";
import { useTenant } from "hooks/useTenant";
import { useValidatingForm } from "hooks/useValidatingForm";
import { DateTime } from "luxon";
import { useCallback, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import invariant from "tiny-invariant";
import { reportStepMetadata } from "util/reportStepMetadata";

import { NotReportingItem, NotReportingModal } from "../Reports/NotReportingModal";
import { OverviewCards } from "./OverviewCards";
import { ReportFloatingBar } from "./ReportFloatingBar";
import { StepperPageHeader } from "./StepperPageHeader";
import {
  GET_REPORT,
  useInvalidSteps,
  useNotStartedSteps,
  useReport,
  useVerifyAndSubmitIssues,
} from "./useReport";
import { useVerifyInputValidation } from "./validationHooks/useVerifyInputValidation";

const SUBMIT_REPORT = gql(`
  mutation SubmitReport($reportId: ID!, $verifier: TierIIReportVerifier!) {
    updateTierIIReportVerifier(reportId: $reportId, verifier: $verifier) {
      id
      organizationStatus
      encampStatus
    }
  }
`);

const LoadingSkeleton = () => {
  const theme = useTheme();

  return (
    <Stack gap={theme.spacing(2)}>
      <Box height={theme.spacing(12)} padding={theme.spacing(2)}>
        <Skeleton
          variant="rectangular"
          style={{
            height: "100%",
          }}
        />
      </Box>

      <Stack paddingBottom={theme.spacing(2)} gap={theme.spacing(2)} paddingX={theme.spacing(2)}>
        <Skeleton variant="rectangular" />
        <Skeleton variant="rectangular" />
        <Skeleton variant="rectangular" />
      </Stack>

      <Stack paddingBottom={theme.spacing(2)} gap={theme.spacing(2)} paddingX={theme.spacing(2)}>
        <Skeleton variant="rectangular" />
      </Stack>

      <Grid container paddingLeft={theme.spacing(2)} paddingBottom={theme.spacing(4)}>
        <Grid item md={6} lg={6} height={theme.spacing(6)}>
          <Skeleton
            variant="rectangular"
            style={{
              height: "100%",
            }}
          />
        </Grid>
      </Grid>
      <Grid container paddingLeft={theme.spacing(2)} paddingBottom={theme.spacing(4)}>
        <Grid item md={6} lg={6} height={theme.spacing(6)}>
          <Skeleton
            variant="rectangular"
            style={{
              height: "100%",
            }}
          />
        </Grid>
      </Grid>
      <Grid container paddingLeft={theme.spacing(2)}>
        <Grid item md={3} lg={3} height={theme.spacing(6)}>
          <Skeleton
            variant="rectangular"
            style={{
              height: "100%",
            }}
          />
        </Grid>
      </Grid>
    </Stack>
  );
};

export type ReportDetails = GetReportDetailsQuery["tierIIReport"];
export type VerifierRow = VerifiersQuery["people"]["items"][number];

type VerifierFieldName = "verifierFullName" | "verifierTitle" | "verifierEmail" | "verifierPhone";
const VERIFIER_FIELDS: VerifierFieldName[] = [
  "verifierFullName",
  "verifierTitle",
  "verifierEmail",
  "verifierPhone",
];
const NO_VERIFIER_ERRORS: Record<VerifierFieldName, boolean> = {
  verifierFullName: false,
  verifierTitle: false,
  verifierEmail: false,
  verifierPhone: false,
};
const ALL_VERIFIER_ERRORS: Record<VerifierFieldName, boolean> = {
  verifierFullName: true,
  verifierTitle: true,
  verifierEmail: true,
  verifierPhone: true,
};

export const VerifyAndSubmit: React.FC = () => {
  const { data: reportData } = useReport();
  const { user, isStaff } = useCurrentUser();
  const { isAmazon } = useTenant();
  const { hasPermissionForFacility } = useAuthorization();
  const navigate = useNavigate();
  if (
    !hasPermissionForFacility(reportData?.tierIIReport?.facility.id ?? "", [
      Permission.VerifyTierIiReport,
    ])
  ) {
    navigate(`..`);
  }
  const theme = useTheme();
  const [showConfirmUpdate, setShowConfirmUpdate] = useState(false);
  const [showNotReportingDialog, setShowNotReportingDialog] = useState(false);
  const [showVerifierConfirmation, setShowVerifierConfirmation] = useState(false);
  const [selectedUserPhones, setSelectedUserPhones] = useState<Phone[]>([]);
  const invalidSteps = useInvalidSteps();
  const incompleteSteps = useNotStartedSteps();
  const alerts = useAlerts();
  const { reportId } = useParams<{ reportId: string }>();
  invariant(reportId);

  const todaysDate = useMemo(() => DateTime.now(), []);

  const report = useMemo(() => {
    return reportData?.tierIIReport;
  }, [reportData]);

  // For annual reports, if we're submitting prior to Jan 1, we enforce
  // a future submission date of Jan 1. This boolean is used for making this determination
  const isEarlyAnnualSubmission = useMemo(() => {
    if (!report) return false;
    const isAnnual = report.reportKind === TierIiReportKind.Annual;
    const isCurrentReport = report.reportingYear === currentTierIIReportingYear;
    const isEarly = todaysDate.year <= currentTierIIReportingYear;
    const state = report.facility.state;

    // CA has their own requirements for submission dates based on anniversaries
    // so we shouldn't enforce a future date.
    if (state === "CA") return false;

    return isAnnual && isCurrentReport && isEarly;
  }, [report, todaysDate.year]);

  const signatureDateIso = useMemo(() => {
    const januaryFirstNextYear = DateTime.local(currentTierIIReportingYear + 1, 1, 1);
    const signatureDate = isEarlyAnnualSubmission ? januaryFirstNextYear : todaysDate;
    return signatureDate.toISODate();
  }, [isEarlyAnnualSubmission, todaysDate]);

  const preSubmitInfoMessage = useMemo(() => {
    if (!isEarlyAnnualSubmission || !report) return;

    const reportingYear = report.reportingYear;
    const signatureYear = reportingYear + 1;

    return `You're verifying your data through December 31, ${reportingYear}. This submission will be signed with a January 1, ${signatureYear} date to meet state requirements.`;
  }, [report, isEarlyAnnualSubmission]);

  const defaultSearch = `tenantId:${reportData?.tierIIReport.tenantId}`;
  const [staffSelectedVerifier, setStaffSelectedVerifier] = useState<VerifierRow | null>();
  const handleRemoveStaffVerifier = () => {
    setStaffSelectedVerifier(null);
    // Clear form fields when user is cleared
    setValue("verifierFullName", null);
    setValue("verifierTitle", null);
    setValue("verifierEmail", null);
    setValue("verifierPhone", null);
    setSelectedUserPhones([]);
    setShownVerifierErrors(NO_VERIFIER_ERRORS);
    trigger(VERIFIER_FIELDS);
  };

  const hasInvalidSteps =
    invalidSteps.filter((s) => s !== TierIiReportStep.VerifyAndSubmit).length > 0;

  const hasIncompleteSteps =
    incompleteSteps.filter(
      (s) => s !== TierIiReportStep.VerifyAndSubmit && s !== TierIiReportStep.ReportOverview
    ).length > 0;

  // Amazon has a hard coded default verifier. These values may change year over year.
  // We also disable editing the verifier fields for Amazon.
  // NJ has a 21-character limit on verifier title, so we use "BEL" instead of the full title.
  const amazonVerifierTitle =
    report?.facility.state === "NJ" ? "BEL" : "Business Environmental Leader";
  const formData = useMemo(
    () => ({
      isVerified: null,
      verifiedAt: signatureDateIso,
      verifierFullName: isAmazon ? "Patricia Sullivan" : null,
      verifierTitle: isAmazon ? amazonVerifierTitle : null,
      verifierEmail: isAmazon ? "epcratier2@amazon.com" : null,
      verifierPhone: isAmazon ? "8005750171" : null,
    }),
    [signatureDateIso, isAmazon, amazonVerifierTitle]
  );
  type ValidationFormData = TierIiReportVerifier & {
    isVerified: boolean | null;
  };

  const { handleSubmit, control, watch, issues, setValue, trigger, clearErrors } =
    useValidatingForm<ValidationFormData>(
      formData,
      useVerifyAndSubmitIssues(),
      useVerifyInputValidation()
    );

  const [submitReport, { loading }] = useMutation(SUBMIT_REPORT, {
    refetchQueries: [GET_REPORT],
  });

  const onSubmit = useCallback(
    async (formState: ValidationFormData) => {
      const { isVerified, ...verifier } = formState;
      if (!isVerified) {
        return;
      }

      const normalizedVerifiedAt = verifier.verifiedAt
        ? (DateTime.fromISO(verifier.verifiedAt).toISO({
            suppressMilliseconds: true,
            includeOffset: false,
          }) ?? verifier.verifiedAt)
        : undefined;

      const verifierInput = {
        ...verifier,
        ...(normalizedVerifiedAt ? { verifiedAt: normalizedVerifiedAt } : {}),
      };

      try {
        await submitReport({
          variables: {
            reportId,
            verifier: verifierInput,
          },
        });
        alerts.success("Successfully submitted report.");
      } catch (err: any) {
        // Check for specific GraphQL error types to provide better user messages
        const graphQLError = err.graphQLErrors?.[0];
        if (graphQLError?.extensions?.code) {
          alerts.error(`Error submitting report: ${graphQLError.message}`);
        } else {
          alerts.error("Error submitting report: ", err);
        }
      }
    },
    [submitReport, alerts, reportId]
  );

  const isVerified = watch("isVerified");
  const verifierFullName = watch("verifierFullName");
  const verifierTitle = watch("verifierTitle");
  const verifierEmail = watch("verifierEmail");
  const verifierPhone = watch("verifierPhone");
  const disabled = useMemo(() => {
    return hasIncompleteSteps || hasInvalidSteps;
  }, [hasIncompleteSteps, hasInvalidSteps]);

  const verifyFieldsDisabled = useMemo(() => {
    return !isVerified;
  }, [isVerified]);

  // A verifier field reveals its error only after it has been engaged — the user
  // blurred it, or a verifier was selected. Once engaged, the field surfaces
  // whatever validation error it has (missing value, or an invalid one such as a
  // too-long title or malformed phone), not just when it's empty.
  const [shownVerifierErrors, setShownVerifierErrors] =
    useState<Record<VerifierFieldName, boolean>>(NO_VERIFIER_ERRORS);
  const handleVerifierFieldBlur = useCallback((field: VerifierFieldName) => {
    setShownVerifierErrors((prev) => ({ ...prev, [field]: true }));
  }, []);

  const signatureDateDisabled = useMemo(() => {
    if (isEarlyAnnualSubmission) return true;
    return disabled || verifyFieldsDisabled;
  }, [isEarlyAnnualSubmission, disabled, verifyFieldsDisabled]);

  const { hasChemicalsToReport, loading: loadingReportingChemicals } = useHasChemicalsToReport();

  // For CA facilities with hazardousMaterialsOnSite=false, they can submit even without chemicals
  const canSubmitWithoutChemicals = useMemo(() => {
    const facilityState = report?.facility.state ?? "";
    const stateFields = report?.facility.stateFields ?? [];
    const hazardousMaterialsOnSite = stateFields.find(
      (field) => field.key === "hazardousMaterialsOnSite"
    )?.value as boolean | undefined;
    return shouldAllowSubmission(facilityState, 0, hazardousMaterialsOnSite);
  }, [report?.facility.state, report?.facility.stateFields]);

  const handlePreSubmit = useCallback(
    (e: React.FormEvent<HTMLFormElement>) => {
      e.preventDefault();

      if (!hasChemicalsToReport) {
        // No chemicals case - show the existing ConfirmDialog
        setShowConfirmUpdate(true);
      } else {
        // Has chemicals case - show the verifier confirmation dialog
        setShowVerifierConfirmation(true);
      }
    },
    [hasChemicalsToReport]
  );

  const VerifierConfirmationDialog = ({
    open,
    onConfirm,
    onCancel,
  }: {
    open: boolean;
    onConfirm: () => void;
    onCancel: () => void;
  }) => (
    <Dialog open={open} onClose={onCancel} maxWidth="xs" fullWidth>
      <DialogTitle
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        Verify &amp; Submit Report
        <IconButton
          aria-label="close"
          onClick={onCancel}
          sx={{
            color: (theme) => theme.palette.grey[500],
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <Typography variant="body1" gutterBottom sx={{ marginBottom: theme.spacing(2) }}>
          Is this verifier information correct?
        </Typography>

        <Grid container spacing={1} sx={{ wordBreak: "break-word", overflowWrap: "break-word" }}>
          <Grid item xs={6} md={4} lg={4}>
            <Typography
              sx={{ fontSize: ".85rem", color: theme.palette.grey[600] }}
              variant="body1"
              gutterBottom
            >
              Full Name
            </Typography>
          </Grid>
          <Grid item xs={6} md={8} lg={8}>
            <Typography variant="body1" gutterBottom>
              {verifierFullName}
            </Typography>
          </Grid>
          <Grid item xs={6} md={4} lg={4}>
            <Typography
              sx={{ fontSize: ".85rem", color: theme.palette.grey[600] }}
              variant="body1"
              gutterBottom
            >
              Title
            </Typography>
          </Grid>
          <Grid item xs={6} md={8} lg={8}>
            <Typography variant="body1" gutterBottom>
              {verifierTitle}
            </Typography>
          </Grid>
          <Grid item xs={6} md={4} lg={4}>
            <Typography
              sx={{ fontSize: ".85rem", color: theme.palette.grey[600] }}
              variant="body1"
              gutterBottom
            >
              Email Address
            </Typography>
          </Grid>
          <Grid item xs={6} md={8} lg={8}>
            <Typography variant="body1" gutterBottom>
              {verifierEmail}
            </Typography>
          </Grid>
          <Grid item xs={6} md={4} lg={4}>
            <Typography
              sx={{ fontSize: ".85rem", color: theme.palette.grey[600] }}
              variant="body1"
              gutterBottom
            >
              Phone Number
            </Typography>
          </Grid>
          <Grid item xs={6} md={8} lg={8}>
            <Typography variant="body1" gutterBottom>
              {verifierPhone}
            </Typography>
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel}>Back</Button>
        <Button onClick={onConfirm} variant="contained" disabled={loading || issues.length > 0}>
          Yes, Submit Report
        </Button>
      </DialogActions>
    </Dialog>
  );

  if (!user?.id) return <LoadingSkeleton />;

  function convertReportForNotReportingModal(report: ReportDetails): NotReportingItem {
    const item: NotReportingItem = {
      reason: NotReportingReason.UnderThreshold,
      facilityId: report.facility.id,
      reportingYear: report.reportingYear,
      tenantId: report.tenantId,
      tierIIReportId: report.id,
      facilityDateClosed: report.facility.dateClosed,
      facilityDateInactive: report.facility.dateInactive,
      colocationReportingFacilityId: report.facility.colocationReportingFacilityId,
      facilityStatus: report.facility.status,
      isNotReporting: report.facility.isNotReporting,
      isNotReportingThroughEncamp: report.facility.isNotReportingThroughEncamp,
    };
    return item;
  }

  // Other-chemicals will fallback to chemicals if that step is not applicable
  const chemicalInventoryPage = `/o/${reportData?.tierIIReport.tenantId}/chemicals/reports/${reportData?.tierIIReport.id}/other-chemicals`;

  return (
    <Stack sx={{ height: "100%" }}>
      <StepperPageHeader
        header={reportStepMetadata.VERIFY_AND_SUBMIT.title}
        description={reportStepMetadata.VERIFY_AND_SUBMIT.getOverviewDescription()}
      />

      <ReportHasNoChemicalsAlert />

      <form
        onSubmit={handlePreSubmit}
        style={{ display: "flex", flex: 1, flexDirection: "column" }}
      >
        <Stack gap={theme.spacing(1)} sx={{ flex: 1 }}>
          {(hasInvalidSteps || hasIncompleteSteps) && (
            <>
              <Stack direction="row" sx={{ marginBottom: theme.spacing(3) }}>
                <ErrorOutline color="error" />
                <Typography paddingLeft={theme.spacing(1)} color={theme.palette.error.main}>
                  The following sections must be completed without any compliance issues before this
                  report can be submitted.
                </Typography>
              </Stack>
              <OverviewCards hideSteps={[TierIiReportStep.VerifyAndSubmit]} hideCompleted={true} />
            </>
          )}

          <Stack sx={{ flex: 1 }}>
            <Typography color={disabled ? colors.grey[600] : undefined}>
              By adding my digital signature below, I acknowledge that my name may appear as the
              official verifier of the Tier II report in the applicable state reporting system. I
              authorize Encamp, Inc. to act as my designated representative for the purpose of
              preparing and submitting this report on my behalf.
              <br />
              <br />
              Note: If I am providing another individual's name as the verifier, I confirm that I
              have the appropriate authority to do so and that this individual has consented to be
              listed as the verifier for this submission
            </Typography>
            <CheckboxField
              sx={{ marginLeft: theme.spacing(1), marginTop: theme.spacing(2) }}
              disabled={disabled}
              noErrorDisplay={true}
              control={control}
              name="isVerified"
              label="I have reviewed this report for accuracy and completion and it is ready to be submitted."
            />

            <Stack spacing={theme.spacing(1)}>
              <Grid container rowSpacing={theme.spacing(3)}>
                {isStaff && !isAmazon && (
                  <Grid item xs={12} paddingBottom="19px">
                    <FormControl fullWidth>
                      <InputLabel
                        shrink
                        htmlFor="staff-verifier-selection"
                        style={{
                          position: "static",
                          transform: "none",
                          marginBottom: "8px",
                        }}
                      >
                        (Staff Only) Selected Verifier:
                      </InputLabel>
                      <Box>
                        {staffSelectedVerifier ? (
                          <Paper
                            variant="outlined"
                            sx={{
                              padding: theme.spacing(1.5),
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "space-between",
                            }}
                          >
                            <Box>
                              <Typography variant="subtitle1" component="div">
                                {`${staffSelectedVerifier.first} ${staffSelectedVerifier.last}${
                                  staffSelectedVerifier.email
                                    ? ` (${staffSelectedVerifier.email})`
                                    : ""
                                }`}
                              </Typography>
                            </Box>
                            <IconButton
                              onClick={handleRemoveStaffVerifier}
                              size="small"
                              aria-label="remove verifier"
                            >
                              <CloseIcon fontSize="small" />
                            </IconButton>
                          </Paper>
                        ) : (
                          <OmnisearchAutocomplete
                            disabled={verifyFieldsDisabled}
                            dataQuery={PERSON_QUERY}
                            defaultSearch={defaultSearch}
                            dataQueryVariables={{
                              sort: [{ field: "last", sort: "asc" }],
                            }}
                            getItems={(data) => data.people.items}
                            onSelectionChange={(selected: VerifierRow | null) => {
                              setStaffSelectedVerifier(selected);
                              if (selected !== null) {
                                const phone24Hour = selected.phones?.find(
                                  (phone) => phone.type === PhoneType.Hrs_24
                                );
                                // the 24 hour number or the first phone
                                const phoneNumber =
                                  phone24Hour?.number ?? selected.phones?.[0]?.number;
                                setValue("verifierFullName", `${selected.first} ${selected.last}`);
                                setValue("verifierTitle", selected.title);
                                setValue("verifierEmail", selected.email);
                                setValue("verifierPhone", phoneNumber);
                                setSelectedUserPhones(selected.phones ?? []);
                                // Clear stale errors before re-validating so populated
                                // fields don't flash, then engage every verifier field
                                // — re-validation surfaces any that are missing or
                                // invalid (e.g. title length, phone format).
                                clearErrors(VERIFIER_FIELDS);
                                setShownVerifierErrors(ALL_VERIFIER_ERRORS);
                              } else {
                                // Clear form fields when user is cleared
                                setValue("verifierFullName", null);
                                setValue("verifierTitle", null);
                                setValue("verifierEmail", null);
                                setValue("verifierPhone", null);
                                setSelectedUserPhones([]);
                                setShownVerifierErrors(NO_VERIFIER_ERRORS);
                              }
                              trigger(VERIFIER_FIELDS);
                            }}
                            renderOption={(props, item: VerifierRow) => (
                              <ListItem key={item.id} {...props} disablePadding>
                                <ListItemText
                                  primary={`${item.first} ${item.last}`}
                                  secondary={`${item.email}`}
                                />
                              </ListItem>
                            )}
                            placeholder="Search"
                            runInitialSearch
                          />
                        )}
                      </Box>
                    </FormControl>
                  </Grid>
                )}
                <Grid item xs={12} lg={6}>
                  <FormTextField
                    disabled={disabled || isAmazon || verifyFieldsDisabled}
                    hideErrors={!shownVerifierErrors.verifierFullName}
                    helperText=" "
                    onBlur={() => handleVerifierFieldBlur("verifierFullName")}
                    textFieldProps={{ required: true }}
                    name="verifierFullName"
                    label="Full Name"
                    control={control}
                  />
                </Grid>
              </Grid>
              <Grid container>
                <Grid item xs={12} lg={6}>
                  <FormTextField
                    disabled={disabled || isAmazon || verifyFieldsDisabled}
                    hideErrors={!shownVerifierErrors.verifierTitle}
                    helperText=" "
                    onBlur={() => handleVerifierFieldBlur("verifierTitle")}
                    textFieldProps={{ required: true }}
                    name="verifierTitle"
                    label="Title"
                    control={control}
                  />
                </Grid>
              </Grid>
              <Grid container>
                <Grid item xs={12} lg={6}>
                  <FormTextField
                    disabled={disabled || isAmazon || verifyFieldsDisabled}
                    hideErrors={!shownVerifierErrors.verifierEmail}
                    helperText=" "
                    onBlur={() => handleVerifierFieldBlur("verifierEmail")}
                    textFieldProps={{ required: true }}
                    name="verifierEmail"
                    label="Email Address"
                    control={control}
                  />
                </Grid>
                {isStaff && selectedUserPhones.length > 0 && (
                  <Grid item md={6} lg={6} sx={{ display: "flex" }}>
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        height: "fit-content",
                        backgroundColor: theme.palette.surfaceSubtle,
                        marginLeft: theme.spacing(2),
                        paddingX: theme.spacing(2.5),
                        paddingY: theme.spacing(1),
                        borderRadius: theme.spacing(1),
                      }}
                    >
                      <Typography variant="body1" sx={{ color: theme.palette.text.secondary }}>
                        Phone Numbers
                      </Typography>
                      {selectedUserPhones.map((phone, index) => (
                        <Typography
                          key={index}
                          variant="body1"
                          sx={{
                            marginBottom: theme.spacing(0.5),
                          }}
                        >
                          {phone.number} ({phone.type})
                        </Typography>
                      ))}
                    </Box>
                  </Grid>
                )}
              </Grid>
              <Grid container>
                <Grid item xs={12} lg={6}>
                  <FormTextField
                    disabled={disabled || isAmazon || verifyFieldsDisabled}
                    hideErrors={!shownVerifierErrors.verifierPhone}
                    helperText=" "
                    onBlur={() => handleVerifierFieldBlur("verifierPhone")}
                    textFieldProps={{ required: true }}
                    name="verifierPhone"
                    label="Phone Number"
                    control={control}
                  />
                  <Grid item xs={12} sm={12} lg={6}>
                    <DateField
                      disabled={signatureDateDisabled}
                      label={isEarlyAnnualSubmission ? "Date of Submission" : "Today's Date"}
                      name="verifiedAt"
                      control={control}
                      helperText={preSubmitInfoMessage}
                    />
                  </Grid>
                </Grid>
                {isStaff && selectedUserPhones.length > 0 && (
                  <Grid item md={6} lg={6} sx={{ display: "flex" }}>
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        height: "fit-content",
                        backgroundColor: theme.palette.surfaceSubtle,
                        marginLeft: theme.spacing(2),
                        paddingX: theme.spacing(2.5),
                        paddingY: theme.spacing(1),
                        borderRadius: theme.spacing(1),
                      }}
                    >
                      <Typography variant="body1" sx={{ color: theme.palette.text.secondary }}>
                        Phone Numbers
                      </Typography>
                      {selectedUserPhones.map((phone, index) => (
                        <Typography
                          key={index}
                          variant="body1"
                          sx={{
                            marginBottom: theme.spacing(0.5),
                          }}
                        >
                          {phone.number} ({phone.type})
                        </Typography>
                      ))}
                    </Box>
                  </Grid>
                )}
              </Grid>
            </Stack>
          </Stack>
        </Stack>
        <ConfirmDialog
          loading={loading}
          open={showConfirmUpdate}
          header="Are you sure you want to submit this report?"
          info={
            <Box>
              {canSubmitWithoutChemicals ? (
                <>
                  There are no chemicals included in this report. Based on your facility
                  information, you may still proceed with submission.
                </>
              ) : (
                <>
                  There are no chemicals included in this report. You may not need to file a Tier II
                  report.
                </>
              )}
            </Box>
          }
          actions={
            <Box
              sx={{
                display: "flex",
                flexWrap: "wrap",
                gap: 1,
                justifyContent: "center",
                width: "100%",
              }}
            >
              <Button
                disabled={loading}
                onClick={() => setShowConfirmUpdate(false)}
                variant="outlined"
              >
                Cancel
              </Button>
              <Button
                disabled={loading}
                onClick={() => navigate(chemicalInventoryPage)}
                variant="outlined"
              >
                Return to Inventory
              </Button>
              <Button
                disabled={loading}
                onClick={() => {
                  setShowConfirmUpdate(false);
                  setShowNotReportingDialog(true);
                }}
                variant={canSubmitWithoutChemicals ? "outlined" : "contained"}
              >
                Mark Facility as Not Reporting
              </Button>
              {canSubmitWithoutChemicals && (
                <Button
                  disabled={loading}
                  onClick={() => {
                    setShowConfirmUpdate(false);
                    handleSubmit(onSubmit)();
                  }}
                  variant="contained"
                >
                  Submit Anyway
                </Button>
              )}
            </Box>
          }
          onConfirm={handleSubmit(onSubmit)}
          onClose={() => setShowConfirmUpdate(false)}
        />
        <VerifierConfirmationDialog
          open={showVerifierConfirmation}
          onConfirm={() => {
            setShowVerifierConfirmation(false);
            handleSubmit(onSubmit)();
          }}
          onCancel={() => setShowVerifierConfirmation(false)}
        />
        <ReportFloatingBar
          saveText="Submit Report"
          overrideSaveTooltip={
            isAmazon && isEarlyAnnualSubmission
              ? `To ensure accurate reporting, the 'Verify and Submit' button is temporarily disabled until the end
              of the current calendar year. Reports for this year must cover the full calendar year, so submission
              will be available starting January 1. Please complete all other sections in the meantime, and you'll
              be able to finalize your report at the beginning of the new year.`
              : preSubmitInfoMessage
          }
          hideCancel
          saveStartIcon={<PublishIcon />}
          issues={issues}
          loading={loading}
          saveDisabled={
            (isAmazon && isEarlyAnnualSubmission) ||
            disabled ||
            !verifierFullName ||
            !verifierTitle ||
            !verifierEmail ||
            !verifierPhone ||
            !isVerified ||
            loadingReportingChemicals ||
            // TODO: this is a bug (ENG-12754). We don't want to disable the button when the only issue is that there
            // are no chemicals over threshold. In this case, we have the "mark as not reporting" flow
            issues.length > 0
          }
          saving={loading}
        />
      </form>
      {reportData?.tierIIReport && !hasChemicalsToReport && (
        <NotReportingModal
          reports={[convertReportForNotReportingModal(reportData.tierIIReport)]}
          open={showNotReportingDialog}
          onClose={() => {
            setShowNotReportingDialog(false);
          }}
        />
      )}
    </Stack>
  );
};
